/**
 * Transmission Method Types
 *
 * This file re-exports types from the centralized types module and provides
 * transmission-specific utility functions.
 *
 * Types are defined in: ../types.ts
 * Aligns with backend: app/defs/correspondence/transmission.py
 */

import type { Transmission } from '@/services/api/correspondenceService';

// ============================================================================
// Transmission Option Utilities
// ============================================================================
import type { TransmissionOption } from '../types';

// ============================================================================
// Re-exports from centralized types module
// ============================================================================
// All type definitions are in ../types.ts - this file provides a convenience
// barrel export for transmission-related code.

export type {
  // API types
  ChequeTransmission,
  ChequeTransmissionFormProps,
  // Custom frontend types - Recipient
  ContactChannel,
  ContactSource,
  ContactSourceType,
  DestinationAddress,
  EmailTransmission,
  EmailTransmissionFormProps,
  FaxTransmission,
  FaxTransmissionFormProps,
  MailingClass,
  MailTransmission,
  MailTransmissionFormProps,
  PostGridMailingClass,
  RecipientContact,
  RecipientEntityType,
  Transmission,
  TransmissionFormBaseProps,
  TransmissionOption,
  // Custom frontend types - Transmission
  TransmissionType,
} from '../types';

/**
 * Check if two transmission options are the same selection.
 */
export function isOptionSelected(
  selected: TransmissionOption | null,
  option: TransmissionOption,
): boolean {
  if (!selected) return false;

  if (selected.type !== option.type) return false;

  if (selected.type === 'manual' && option.type === 'manual') {
    return selected.channel === option.channel;
  }

  if (selected.type === 'contact' && option.type === 'contact') {
    return (
      selected.contact.id === option.contact.id &&
      selected.channel === option.channel
    );
  }

  return false;
}

// ============================================================================
// Empty Transmission Factory
// ============================================================================

/** Helper to create empty transmission of a given type */
export function createEmptyTransmission(
  type: Transmission['type'],
): Partial<Transmission> {
  const base = { display_name: '', source: undefined };

  switch (type) {
    case 'fax':
      return { ...base, type: 'fax', fax_number: '', cover_message: undefined };
    case 'email':
      return {
        ...base,
        type: 'email',
        email: '',
        subject: undefined,
        body: undefined,
        cc: undefined,
      };
    case 'mailed_letter':
      return {
        ...base,
        type: 'mailed_letter',
        address: {
          address_line1: '',
          city: '',
          state: '',
          zip_code: '',
          country: 'US',
        },
        mailing_class: 'first_class',
        include_return_envelope: false,
      };
    case 'mailed_cheque':
      return {
        ...base,
        type: 'mailed_cheque',
        address: {
          address_line1: '',
          city: '',
          state: '',
          zip_code: '',
          country: 'US',
        },
        amount: '',
        memo: undefined,
      };
    default:
      return base;
  }
}
